import { type RefCallback, useCallback, useRef, useState } from "react";

import type { SystemStyleObject } from "@hightouch/htui";

const FADE_WIDTH_PX = 24;

export const useHorizontalScrollFade = ({
  observeContent = false,
}: {
  /**
   * Also watch the container's single content wrapper, for content that can
   * grow while the container stays put (e.g. a pill expanding in a full row).
   */
  observeContent?: boolean;
} = {}): {
  ref: RefCallback<HTMLDivElement>;
  sx: SystemStyleObject;
} => {
  const elRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const update = useCallback(() => {
    const el = elRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const isScrollable = scrollWidth > clientWidth;

    setShowLeft(isScrollable && scrollLeft > 1);
    setShowRight(isScrollable && scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const refCallback: RefCallback<HTMLDivElement> = useCallback(
    (el: HTMLDivElement | null) => {
      const prevEl = elRef.current;
      if (prevEl) {
        prevEl.removeEventListener("scroll", update);
        observerRef.current?.disconnect();
        observerRef.current = null;
      }

      elRef.current = el;

      if (el) {
        el.addEventListener("scroll", update, { passive: true });
        // Absent in jsdom; the fade is cosmetic, so it just stays static there.
        if (typeof ResizeObserver !== "undefined") {
          const observer = new ResizeObserver(update);
          observer.observe(el);
          if (observeContent && el.firstElementChild) {
            observer.observe(el.firstElementChild);
          }
          observerRef.current = observer;
        }
        update();
      }
    },
    [update, observeContent],
  );

  const maskImage =
    showLeft || showRight
      ? [
          showLeft
            ? `linear-gradient(to right, transparent, black ${FADE_WIDTH_PX}px)`
            : "linear-gradient(black, black)",
          showRight
            ? `linear-gradient(to left, transparent, black ${FADE_WIDTH_PX}px)`
            : "linear-gradient(black, black)",
        ].join(", ")
      : undefined;

  return {
    ref: refCallback,
    sx: {
      maskImage,
      maskSize: maskImage ? "51% 100%, 51% 100%" : undefined,
      maskPosition: maskImage ? "left, right" : undefined,
      maskRepeat: "no-repeat",
      WebkitMaskImage: maskImage,
      WebkitMaskSize: maskImage ? "51% 100%, 51% 100%" : undefined,
      WebkitMaskPosition: maskImage ? "left, right" : undefined,
      WebkitMaskRepeat: "no-repeat",
    },
  };
};
